import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ConciergeWidget from "@/components/ConciergeWidget";

import Home from "@/pages/Home";
import Marketplace from "@/pages/Marketplace";
import TemplateDemo from "@/pages/TemplateDemo";
import TemplatePreview from "@/pages/TemplatePreview";
import BareTemplate from "@/pages/BareTemplate";
import Solutions from "@/pages/Solutions";
import SolutionDetail from "@/pages/SolutionDetail";
import TemplateDetail from "@/pages/TemplateDetail";
import Services from "@/pages/Services";
import LaunchPackage from "@/pages/LaunchPackage";
import Questionnaire from "@/pages/Questionnaire";
import ConsultationBooking from "@/pages/ConsultationBooking";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import CheckoutStatus from "@/pages/CheckoutStatus";
import EstimateRequest from "@/pages/EstimateRequest";
import ClientPortal from "@/pages/ClientPortal";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AiAssistantPage from "@/pages/AiAssistant";
import AIConciergePage from "@/pages/AIConciergePage";
import BusinessPlanLanding from "@/pages/BusinessPlanLanding";
import PlanYourBusiness from "@/pages/PlanYourBusiness";
import StartYourBusiness from "@/pages/StartYourBusiness";
import AiBotsPricing from "@/pages/AiBotsPricing";
import PublicSitePreview from "@/pages/PublicSitePreview";
import DeviceShowcase from "@/pages/DeviceShowcase";
import PublicProposal from "@/pages/PublicProposal";
import BotChat from "@/pages/BotChat";
import VeteransInitiative from "@/pages/VeteransInitiative";
import VeteransHub from "@/pages/VeteransHub";
import CohortRequest from "@/pages/CohortRequest";
import CohortOverview from "@/pages/CohortOverview";import VeteransPriorService from "@/pages/VeteransPriorService";
import Grow from "@/pages/Grow";
import FundingReadiness from "@/pages/FundingReadiness";
import StarterKit from "@/pages/StarterKit";
import NotFound from "@/pages/NotFound";
import { Privacy, Terms, Refund } from "@/pages/Legal";

import { StudioDataProvider } from "@/pages/studio/StudioContext";
import StudioLayout from "@/pages/studio/StudioLayout";
import StudioDashboard from "@/pages/studio/StudioDashboard";
import MissionIntelligence from "@/pages/studio/MissionIntelligence";
import AIIntelligence from "@/pages/studio/AIIntelligence";
import CommandCenter from "@/pages/studio/CommandCenter";
import ExecutiveIntelligence from "@/pages/studio/ExecutiveIntelligence";
import IntelligenceRouter from "@/pages/studio/IntelligenceRouter";
import Subscribers from "@/pages/studio/Subscribers";
import StudioMissions from "@/pages/studio/Missions";
import StudioStrategists from "@/pages/studio/Strategists";
import StudioKnowledge from "@/pages/studio/Knowledge";
import StudioKnowledgeManager from "@/pages/studio/KnowledgeManager";
import StudioDepartments from "@/pages/studio/Departments";
import StudioWorkflows from "@/pages/studio/Workflows";
import StudioAnalytics from "@/pages/studio/Analytics";
import StudioProviders from "@/pages/studio/Providers";
import StrategistBuilder from "@/pages/studio/StrategistBuilder";
import MissionManager from "@/pages/studio/MissionManager";
import DepartmentManager from "@/pages/studio/DepartmentManager";
import WorkflowBuilder from "@/pages/studio/WorkflowBuilder";
import CostDashboard from "@/pages/studio/CostDashboard";
import RoiDashboard from "@/pages/studio/RoiDashboard";
import ProviderManager from "@/pages/studio/ProviderManager";
import ExportCenter from "@/pages/studio/ExportCenter";
import MemoryCenter from "@/pages/studio/MemoryCenter";
import EnterpriseSettings from "@/pages/studio/EnterpriseSettings";
import EvaluationCenter from "@/pages/studio/EvaluationCenter";
import TrainingCenter from "@/pages/studio/TrainingCenter";
import AutomationCenter from "@/pages/studio/AutomationCenter";
import AuditCenter from "@/pages/studio/AuditCenter";

function PublicLayout() {
  return (
    <div className="min-h-screen bg-navy flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20"><Outlet /></main>
      <Footer />
      <ConciergeWidget />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Scroll to the very top on every route change so every page/detail/demo begins at the top.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);
  // Handle Google OAuth callback returning with #session_id= on any route
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/template/:slug" element={<TemplatePreview />} />
        <Route path="/marketplace/demo/:slug" element={<TemplateDemo />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/solutions/:slug" element={<SolutionDetail />} />
        <Route path="/template/:id" element={<TemplateDetail />} />
        <Route path="/services" element={<Services />} />
        <Route path="/funding-readiness" element={<FundingReadiness />} />
        <Route path="/launch-package" element={<LaunchPackage />} />
        <Route path="/ai-assistant" element={<AiAssistantPage />} />
        <Route path="/ai-concierge" element={<AIConciergePage />} />
        <Route path="/business-plan" element={<BusinessPlanLanding />} />
        <Route path="/plan" element={<PlanYourBusiness />} />
        <Route path="/start-your-business" element={<StartYourBusiness />} />
        <Route path="/ai-chat-assistant" element={<AiBotsPricing />} />
        <Route path="/veterans" element={<VeteransHub />} />
        <Route path="/veterans/transitioning" element={<VeteransInitiative />} />
        <Route path="/veterans/prior-service" element={<VeteransPriorService />} />
        <Route path="/veterans/cohort" element={<CohortRequest />} />
        <Route path="/veterans/cohort/overview" element={<CohortOverview />} />
        <Route path="/grow" element={<Grow />} />
        <Route path="/starter-kit" element={<StarterKit />} />
        <Route path="/start" element={<Questionnaire />} />
        <Route path="/get-started" element={<Navigate to="/start" replace />} />
        <Route path="/book" element={<ConsultationBooking />} />
        <Route path="/checkout/success" element={<CheckoutStatus />} />
        <Route path="/checkout/cancel" element={<CheckoutStatus canceled />} />
        <Route path="/estimate/:slug" element={<EstimateRequest />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/refund" element={<Refund />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="/t/:slug" element={<BareTemplate />} />
      <Route path="/bot/:publicKey" element={<BotChat />} />
      <Route path="/proposal/:token" element={<PublicProposal />} />
      <Route path="/preview/:token" element={<PublicSitePreview />} />
      <Route path="/showcase/:token" element={<DeviceShowcase />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/portal" element={<ProtectedRoute><ClientPortal /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/studio" element={<ProtectedRoute adminOnly><StudioDataProvider><StudioLayout /></StudioDataProvider></ProtectedRoute>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<StudioDashboard />} />
        <Route path="mission-intelligence" element={<MissionIntelligence />} />
        <Route path="ai-intelligence" element={<AIIntelligence />} />
        <Route path="command-center" element={<CommandCenter />} />
        <Route path="executive-intelligence" element={<ExecutiveIntelligence />} />
        <Route path="intelligence-router" element={<IntelligenceRouter />} />
        <Route path="subscribers" element={<Subscribers />} />
        <Route path="missions" element={<StudioMissions />} />
        <Route path="strategists" element={<StudioStrategists />} />
        <Route path="knowledge" element={<StudioKnowledge />} />
        <Route path="knowledge-manager" element={<StudioKnowledgeManager />} />
        <Route path="strategist-builder" element={<StrategistBuilder />} />
        <Route path="mission-manager" element={<MissionManager />} />
        <Route path="department-manager" element={<DepartmentManager />} />        <Route path="workflow-builder" element={<WorkflowBuilder />} /><Route path="departments" element={<StudioDepartments />} />
        <Route path="workflows" element={<StudioWorkflows />} />
        <Route path="analytics" element={<StudioAnalytics />} />
        <Route path="cost-dashboard" element={<CostDashboard />} />
        <Route path="roi" element={<RoiDashboard />} />
        <Route path="resource-manager" element={<ProviderManager />} />
        <Route path="export-center" element={<ExportCenter />} />
        <Route path="memory-center" element={<MemoryCenter />} />
        <Route path="enterprise-settings" element={<EnterpriseSettings />} />
        <Route path="evaluation-center" element={<EvaluationCenter />} />
        <Route path="training-center" element={<TrainingCenter />} />
        <Route path="automation-center" element={<AutomationCenter />} />
        <Route path="audit-center" element={<AuditCenter />} />
        <Route path="providers" element={<StudioProviders />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Helmet defaultTitle="ATF WebWorks | AI-Powered Websites & Business Growth" />
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" theme="dark" richColors />
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}
